// src/App.js
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import WhatsAppFloatingButton from './components/WhatsAppFloatingButton';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Home from './pages/HomeNew'; // New modern design
import AI from './pages/AI';
import Progress from './pages/Progress';
import ParentDashboard from './pages/ParentDashboard';
import About from './pages/About';
import Dashboard from './pages/Dashboard';
import Checkout from './pages/Checkout';
import Login from './pages/Login';
import Register from './pages/Register';
import FreeSessionRegistration from './pages/FreeSessionRegistration';
import InstructorApply from './pages/InstructorApply';

// New Integrated Pages
import FinalQuizzesList from './pages/FinalQuizzesList';
import TakeQuiz from './pages/TakeQuiz';
import StudentPortal from './pages/StudentPortalNew'; // New modern portal

// Admin Pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminStudents from './pages/admin/AdminStudents';
import AdminQuizzes from './pages/admin/AdminQuizzes';
import AdminResults from './pages/admin/AdminResults';
import ManageQuestions from './pages/admin/ManageQuestions';
import SendNotifications from './pages/admin/SendNotifications';
import AdminFreeSessions from './pages/admin/AdminFreeSessions';
import AdminFinance from './pages/admin/AdminFinance';
import StudentFinancialProfile from './pages/admin/StudentFinancialProfile';
import AdminHistory from './pages/admin/AdminHistory';
import InstructorPayroll from './pages/admin/InstructorPayroll';
import AdminGroups from './pages/admin/AdminGroups';
import AdminAuditLogs from './pages/admin/AdminAuditLogs';
import AdminInstructors from './pages/admin/AdminInstructors';
import AdminInstructorApplications from './pages/admin/AdminInstructorApplications';
import AdminSessionReport from './pages/admin/AdminSessionReport';
import InstructorDashboard from './pages/admin/InstructorDashboard';
import AdminAcademics from './pages/admin/AdminAcademics';
import StudentAcademics from './pages/StudentAcademics';

import './index.css';
import './components/component-library.css'; // Code Wave Component Library

export default function App() {
  return <AppContent />;
}

function AppContent() {
  const location = useLocation();
  const isAIRoute = location.pathname === '/ai-tutor';
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isInstructorRoute = location.pathname.startsWith('/instructor');
  const isAuthRoute = ['/login', '/register', '/free-session'].includes(location.pathname);

  const getMainClass = () => {
    if (isInstructorRoute) return 'instructor-root-container';
    if (isAdminRoute) return 'admin-root-container';
    if (isAuthRoute) return 'main-area auth-main-area';
    return `main-area ${isAIRoute ? 'ai-page-main' : ''}`;
  };

  return (
    <div className="app-root">
      {!isAdminRoute && !isInstructorRoute && <ScrollToTop />}
      {!isAdminRoute && !isInstructorRoute && <Header />}

      <main className={getMainClass()}>
        <Routes>
          {/* صفحات مفتوحة */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/free-session" element={<FreeSessionRegistration />} />
          <Route path="/apply" element={<InstructorApply />} />

          {/* صفحات محمية (طلاب ومنتسبين) */}
          <Route path="/lessons" element={<ProtectedRoute roles={['student']}><StudentAcademics /></ProtectedRoute>} />
          <Route path="/legacy-lessons" element={<Navigate to="/lessons" replace />} />
          <Route path="/ai-tutor" element={<ProtectedRoute><AI /></ProtectedRoute>} />
          <Route path="/quizzes" element={<Navigate to="/lessons" replace />} />

          {/* Final Quiz System */}
          <Route path="/final-quiz" element={<ProtectedRoute roles={['student']}><FinalQuizzesList /></ProtectedRoute>} />
          <Route path="/take-quiz/:quizId" element={<ProtectedRoute roles={['student']}><TakeQuiz /></ProtectedRoute>} />

          {/* Admin System */}
          <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="students" element={<AdminStudents />} />
            <Route path="exams" element={<AdminQuizzes />} />
            <Route path="exams/:quizId/questions" element={<ManageQuestions />} />
            <Route path="results" element={<AdminResults />} />
            <Route path="notifications" element={<SendNotifications />} />
            <Route path="free-sessions" element={<AdminFreeSessions />} />
            <Route path="lessons" element={<Navigate to="/admin/academics" replace />} />
            <Route path="finance" element={<AdminFinance />} />
            <Route path="finance/student/:id" element={<StudentFinancialProfile />} />
            <Route path="instructor-payroll" element={<InstructorPayroll />} />
            <Route path="instructors" element={<AdminInstructors />} />
            <Route path="groups" element={<AdminGroups />} />
            <Route path="history" element={<AdminHistory />} />
            <Route path="audit-logs" element={<AdminAuditLogs />} />
            <Route path="instructor-applications" element={<AdminInstructorApplications />} />
            <Route path="session-report" element={<AdminSessionReport />} />
            <Route path="academics" element={<AdminAcademics />} />
            <Route path="attendance" element={<AdminAcademics defaultTab="attendance" />} />
          </Route>

          {/* Instructor Portal */}
          <Route
            path="/instructor/*"
            element={
              <ProtectedRoute roles={['instructor']}>
                <InstructorDashboard />
              </ProtectedRoute>
            }
          />

          <Route path="/student-portal" element={<ProtectedRoute roles={['student']}><StudentPortal /></ProtectedRoute>} />
          <Route path="/my-learning" element={<ProtectedRoute roles={['student']}><StudentAcademics /></ProtectedRoute>} />
          <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
          <Route path="/parent" element={<ParentDashboard />} />

          {/* سلة المشتريات والاشتراكات */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/checkout" element={<Checkout />} />
        </Routes>
      </main>

      {!isAdminRoute && !isInstructorRoute && !isAIRoute && <Footer />}
      {location.pathname !== '/register' && <WhatsAppFloatingButton />}
    </div>
  );
}
